<template>
  <div>
    <!-- Dialog de Histórico (lista de práticas) — oculto no modo openDirectAdd -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-run</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="white" @click="openAdd" class="mr-1" v-bind="attrs" v-on="on">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Nova prática</span>
            </v-tooltip>
            <v-btn icon color="white" class="close-btn" @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="practices.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">Modalidade</th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">Dias</th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 140px">Horário</th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 140px">Tipo</th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in practices" :key="item.id" class="history-row">
                  <td class="pa-4">
                    <span class="body-2 font-weight-medium">{{ item.modality || "—" }}</span>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ daysShort(item.days_of_week) }}</span>
                  </td>
                  <td class="text-no-wrap pa-4">
                    <span class="body-2">
                      <template v-if="item.start_time">
                        {{ item.start_time }}<span v-if="item.end_time">–{{ item.end_time }}</span>
                      </template>
                      <template v-else>—</template>
                    </span>
                  </td>
                  <td class="pa-4">
                    <v-chip
                      x-small
                      :color="item.structure_type === 'structured' ? 'green darken-1' : 'orange darken-2'"
                      text-color="white"
                    >
                      {{ item.structure_type === "structured" ? "Estruturado" : "Não estruturado" }}
                    </v-chip>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small color="catGreen" class="action-btn mr-1" @click="openEdit(item)" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small color="error" class="action-btn" @click="confirmDelete(item)" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-run-fast</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhuma prática registrada.</p>
            <v-btn color="catGreen" outlined small class="mt-4" @click="openAdd">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeira prática
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar/Editar (uma prática) -->
    <v-dialog v-model="formDialogVisible" max-width="640px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header d-flex justify-space-between align-center">
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">{{ editingId ? "mdi-pencil" : "mdi-plus-circle-outline" }}</v-icon>
            <span class="ds-dialog-title">{{ editingId ? "Editar prática" : "Nova prática" }}</span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- Obrigatórios vazios ficam em vermelho ao tentar salvar (validate). -->
          <v-form ref="entryForm" @submit.prevent>
            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-run</v-icon>
                  <span>Prática</span>
                </div>
              </div>
              <div class="text-subtitle-2 font-weight-bold mb-1">Tipo de prática</div>
              <v-radio-group v-model="form.structure_type" row hide-details class="mt-0 mb-4">
                <v-radio label="Estruturado" value="structured" color="catGreen" />
                <v-radio label="Não estruturado" value="unstructured" color="orange" />
              </v-radio-group>

              <v-text-field
                v-model="form.modality"
                label="Modalidade *"
                placeholder="ex.: Musculação, Corrida…"
                outlined
                dense
                color="catGreen"
                hide-details="auto"
                :rules="[(v) => !!(v && v.trim()) || 'Informe a modalidade']"
              ></v-text-field>
            </section>

            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                  <span>Dias e Horário</span>
                  <span class="ds-form-section__optional">opcional</span>
                </div>
              </div>
              <v-select
                v-model="form.days_of_week"
                :items="dayOptions"
                item-text="label"
                item-value="value"
                label="Dias da semana"
                multiple
                chips
                small-chips
                outlined
                dense
                color="catGreen"
                item-color="catGreen"
                hide-details="auto"
                class="mb-3"
              ></v-select>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.start_time"
                    :items="timeOptions"
                    label="Hora início"
                    outlined
                    dense
                    clearable
                    color="catGreen"
                    item-color="catGreen"
                    hide-details="auto"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.end_time"
                    :items="timeOptions"
                    label="Hora fim"
                    :rules="[endAfterStart]"
                    outlined
                    dense
                    clearable
                    color="catGreen"
                    item-color="catGreen"
                    hide-details="auto"
                  ></v-select>
                </v-col>
              </v-row>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeForm">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="catGreen" dark :loading="saving" depressed class="px-6" @click="saveForm">
            <v-icon small left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";

const DAY_OPTIONS = [
  { label: "Seg", value: "mon" },
  { label: "Ter", value: "tue" },
  { label: "Qua", value: "wed" },
  { label: "Qui", value: "thu" },
  { label: "Sex", value: "fri" },
  { label: "Sáb", value: "sat" },
  { label: "Dom", value: "sun" },
];

function blankForm() {
  return {
    structure_type: "structured",
    modality: "",
    days_of_week: [],
    start_time: null,
    end_time: null,
  };
}

export default {
  name: "EditExercisePracticesDialog",

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "Práticas de exercício" },
    practices: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      editingId: null,
      form: blankForm(),
      saving: false,
      dayOptions: DAY_OPTIONS,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    timeOptions() {
      const out = [];
      for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
          out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
      }
      return out;
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAdd());
      }
    },
    formDialogVisible(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    daysShort(days) {
      if (!Array.isArray(days) || days.length === 0) return "—";
      return days
        .map((d) => DAY_OPTIONS.find((o) => o.value === d)?.label || d)
        .join(", ");
    },

    openAdd() {
      this.editingId = null;
      this.form = blankForm();
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    openEdit(item) {
      this.editingId = item.id;
      this.form = {
        structure_type: item.structure_type || "structured",
        modality: item.modality || "",
        days_of_week: [...(item.days_of_week || [])],
        start_time: item.start_time || null,
        end_time: item.end_time || null,
      };
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    closeForm() {
      this.formDialogVisible = false;
      this.editingId = null;
      this.form = blankForm();
    },

    /** Hora fim depois do início (os dois são opcionais). */
    endAfterStart(v) {
      const ini = this.form.start_time;
      return !v || !ini || v > ini || "A hora de fim deve ser depois do início";
    },

    async saveForm() {
      // Campos inválidos ficam em vermelho; não grava.
      if (!this.$refs.entryForm.validate()) return;
      this.saving = true;
      const payload = {
        structure_type: this.form.structure_type,
        modality: this.form.modality.trim(),
        days_of_week: this.form.days_of_week,
        start_time: this.form.start_time || null,
        end_time: this.form.end_time || null,
      };
      try {
        if (this.editingId) {
          await http.put(`/api/exercise-practices/update/${this.editingId}`, payload);
        } else {
          await http.post("/api/exercise-practices/create", {
            patient_id: this.patientId,
            ...payload,
          });
        }
        this.success("Prática salva com sucesso!");
        this.closeForm();
        this.$emit("data-saved", payload);
      } catch (err) {
        console.error("Erro ao salvar prática:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar a prática");
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(item) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja remover a prática "${item.modality || ""}"?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) return;
      try {
        await http.delete(`/api/exercise-practices/${item.id}`);
        this.success("Prática removida com sucesso!");
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir prática:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover a prática");
      }
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18), 0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}
.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}
.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
.history-table thead th {
  background-color: var(--ds-primary-surface) !important;
  border-bottom: 2px solid var(--ds-primary-border) !important;
  letter-spacing: 0.4px;
  color: var(--ds-primary-deep) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}
.history-row:hover {
  background-color: var(--ds-primary-surface) !important;
}
.history-row td {
  border-bottom: 1px solid var(--ds-primary-border) !important;
  color: var(--ds-text-600);
}
.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}
.action-btn:hover {
  transform: scale(1.15);
}
</style>
